import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { formatPhoneNumber } from '../utils/formatters';

interface Practice {
  uuid: string;
  name: string;
  description?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  slug?: string;
  specialties: string[];
  accepts_new_patients: boolean;
}

const STATE_NAMES: { [key: string]: string } = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
};

const PracticesByState: React.FC = () => {
  const { state } = useParams<{ state: string }>();
  const navigate = useNavigate();
  const [practices, setPractices] = useState<Practice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Group practices by city
  const practicesByCity = practices.reduce((acc, practice) => {
    const city = practice.city || 'Other';
    if (!acc[city]) {
      acc[city] = [];
    }
    acc[city].push(practice);
    return acc;
  }, {} as { [key: string]: Practice[] });

  const cities = Object.keys(practicesByCity).sort();

  useEffect(() => {
    const fetchPractices = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/practices/state/${state}`
        );

        if (!response.ok) {
          if (response.status === 404) {
            setError('No practices found in this state');
          } else {
            setError('Failed to load practices');
          }
          return;
        }

        const data = await response.json();
        setPractices(data);
      } catch (err) {
        console.error('Error fetching practices:', err);
        setError('Failed to load practices');
      } finally {
        setLoading(false);
      }
    };

    if (state) {
      fetchPractices();
    }
  }, [state]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading practices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Oops!</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/practices')}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to all practices
          </button>
        </div>
      </div>
    );
  }

  const stateName = STATE_NAMES[state?.toUpperCase() || ''] || state;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Breadcrumb Schema for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://helppet.ai"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Practices",
              "item": "https://helppet.ai/practices"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": STATE_NAMES[state?.toUpperCase() || ''] || state,
              "item": `https://helppet.ai/practice/${state}`
            }
          ]
        })}
      </script>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/practices"
            className="text-blue-600 hover:text-blue-700 font-medium mb-4 inline-block"
          >
            ← All Practices
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Veterinary Practices in {stateName}
          </h1>
          <p className="text-xl text-gray-600">
            {practices.length} {practices.length === 1 ? 'practice' : 'practices'} found
          </p>
        </div>

        {/* Cities Index */}
        {cities.length > 1 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Browse by City</h2>
            <div className="flex flex-wrap gap-2">
              {cities.map((city) => (
                <Link
                  key={city}
                  to={`/practice/${state}/${city}`}
                  className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  {city} ({practicesByCity[city].length})
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Practices grouped by city */}
        {cities.map((city) => (
          <div key={city} className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">{city}</h2>
              {practicesByCity[city].length > 1 && (
                <Link
                  to={`/practice/${state}/${city}`}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  View all in {city} →
                </Link>
              )}
            </div>
            
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {practicesByCity[city].map((practice) => (
                <div
                  key={practice.uuid}
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {practice.name}
                  </h3>
                  
                  {practice.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {practice.description}
                    </p>
                  )}

                  {practice.specialties && practice.specialties.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {practice.specialties.slice(0, 3).map((specialty, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    {practice.address && (
                      <p className="truncate">{practice.address}</p>
                    )}
                    {practice.phone && (
                      <p>{formatPhoneNumber(practice.phone)}</p>
                    )}
                  </div>

                  {practice.accepts_new_patients && (
                    <div className="mb-4">
                      <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                        ✓ Accepting New Patients
                      </span>
                    </div>
                  )}

                  <Link
                    to={
                      practice.slug && practice.city && practice.state
                        ? `/practice/${practice.state}/${practice.city}/${practice.slug}`
                        : `/practices/${practice.uuid}`
                    }
                    className="block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    View Details
                  </Link>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PracticesByState;

